import {
    DatacentralHookForm,
    type DatacentralHookFormDataItem,
} from '@/components/ui/form-field/datacentral-hook-form'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import {
    createPricingStep,
    updatePricingStep,
} from '@/modules/Settings/services/product-pricing-step'
import {
    CreatePricingStepPayload,
    ProductPricingStep,
} from '@/modules/Settings/types/product'
import { ModalProps } from '@/types/shared'
import { Loader2, Save } from 'lucide-react'
import React, { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'vnetwork-ui'

export interface CreateEditPricingStepModalProps extends ModalProps {
    productId: number
    optionId: number
    pricingStep?: ProductPricingStep | null
}

const CreateEditPricingStepModal: React.FC<CreateEditPricingStepModalProps> = ({
    productId,
    optionId,
    pricingStep,
    onClose,
    open,
}) => {
    const { t } = useTranslation('settings')
    const isEdit = !!pricingStep

    const formControl = useForm<CreatePricingStepPayload>({
        defaultValues: pricingStep
            ? {
                  price_start: pricingStep.price_start,
                  price_end: pricingStep.price_end,
                  price_per_unit: pricingStep.price_per_unit,
              }
            : {
                  price_start: 0,
                  price_end: 0,
                  price_per_unit: 0,
              },
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: async (data: CreatePricingStepPayload) => {
                if (isEdit && pricingStep) {
                    return updatePricingStep(
                        productId,
                        optionId,
                        pricingStep.product_pricing_step_id,
                        data
                    )
                }
                return createPricingStep(productId, optionId, data)
            },
        },
        {
            successMessage: isEdit
                ? t('pricing-step-updated-successfully')
                : t('pricing-step-created-successfully'),
            reactQueryKeys: [
                QUERY_KEYS.PRODUCT_CONFIG_OPTIONS,
                QUERY_KEYS.PRODUCT_PRICING_STEPS,
            ],
            onSuccess: () => onClose(),
        }
    )

    const formData: DatacentralHookFormDataItem<CreatePricingStepPayload>[] =
        useMemo(
            () => [
                {
                    formItemProps: {
                        name: 'price_start',
                        type: 'number',
                        label: t('price-start'),
                        placeholder: '0',
                        required: true,
                        min: 0,
                    },
                    colSpan: 4,
                },
                {
                    formItemProps: {
                        name: 'price_end',
                        type: 'number',
                        label: t('price-end'),
                        placeholder: '1000',
                        required: true,
                        min: 0,
                    },
                    colSpan: 4,
                },
                {
                    formItemProps: {
                        name: 'price_per_unit',
                        type: 'number',
                        label: t('price-per-unit'),
                        placeholder: '0.1',
                        required: true,
                        min: 0,
                    },
                    colSpan: 4,
                },
            ],
            [t]
        )

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={isEdit ? t('edit-pricing-step') : t('create-pricing-step')}
            maxWidth="sm"
        >
            <DatacentralHookForm<CreatePricingStepPayload>
                formControl={formControl}
                data={formData}
                onSubmit={(data) => mutateAsync(data)}
                columns={12}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outline',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: isEdit ? t('update') : t('create'),
                        type: 'submit',
                        variant: 'default',
                        disabled: isPending,
                        loading: isPending,
                        startIcon: isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="h-4 w-4" />
                        ),
                    },
                ]}
            />
        </Dialog>
    )
}

export default CreateEditPricingStepModal
